import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import WebApp from '@twa-dev/sdk';
import AuthContext from "../context/auth.context";
import useAuth from "../hooks/auth.hook";
import ProviderRoutes from "./ProviderRoutes";
import useHttp from "../hooks/http.hook";
import './App.css';


function getTelegramInitData() {
    try {
        return String(WebApp.initData || window.Telegram?.WebApp?.initData || "");
    } catch (_) {
        return "";
    }
}

function setupTelegramWebApp() {
    const calls = [
        () => WebApp.ready(),
        () => WebApp.expand(),
        () => WebApp.disableVerticalSwipes(),
        () => WebApp.enableClosingConfirmation(),
        () => WebApp.requestFullscreen(),
    ];
    for (const call of calls) {
        try {
            call();
        } catch (_) {
            // Older Telegram clients may not support every WebApp method.
        }
    }
}

function Spinner() {
    return (
        <div className="app-spinnerWrap">
            <svg width="56" height="56" viewBox="0 0 50 50" aria-label="Loading" role="status">
                <circle
                    cx="25"
                    cy="25"
                    r="20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeDasharray="31.415, 31.415"
                >
                    <animateTransform
                        attributeName="transform"
                        type="rotate"
                        from="0 25 25"
                        to="360 25 25"
                        dur="1s"
                        repeatCount="indefinite"
                    />
                </circle>
            </svg>
        </div>
    );
}

function AuthError({ message, onRetry }) {
    return (
        <div className="app-authError">
            <p>{message}</p>
            <button type="button" className="app-authErrorButton" onClick={onRetry}>
                Повторить
            </button>
        </div>
    );
}

function App() {
    const { token, login, logout, ready } = useAuth();
    const isAuthenticated = !!token;
    
    const { request } = useHttp();
    const didAuthRequest = useRef(false);
    const authContextValue = useMemo(
        () => ({ token, login, logout, isAuthenticated }),
        [token, login, logout, isAuthenticated]
    );

    const [authLoading, setAuthLoading] = useState(true);
    const [authError, setAuthError] = useState(null);
    const [authRetryCount, setAuthRetryCount] = useState(0);
    const [showBrowserLogin, setShowBrowserLogin] = useState(false);

    useEffect(() => {
        // On mobile browsers/WebViews (incl. Telegram), `position: fixed` footers
        // often jump above the software keyboard. We hide fixed footers while
        // the keyboard is open so actions stay "below" it (off-screen).
        const root = document.documentElement;

        const isCoarsePointer =
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(pointer: coarse)').matches;

        const isSmallViewport =
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(max-width: 900px)').matches;

        const shouldHandle = isCoarsePointer || isSmallViewport;
        if (!shouldHandle) return;

        const isTextLikeInput = (el) => {
            if (!el) return false;
            if (el.isContentEditable) return true;
            const tag = String(el.tagName || '').toLowerCase();
            if (tag === 'textarea' || tag === 'select') return true;
            if (tag !== 'input') return false;

            const type = String(el.getAttribute('type') || 'text').toLowerCase();
            // Inputs that typically don't open the soft keyboard:
            const nonKeyboardTypes = new Set([
                'button',
                'submit',
                'reset',
                'checkbox',
                'radio',
                'range',
                'file',
                'color',
                'image',
                'hidden',
            ]);
            if (nonKeyboardTypes.has(type)) return false;
            return !el.disabled && !el.readOnly;
        };

        const setKeyboardOpen = (open) => {
            root.classList.toggle('provider-keyboard-open', !!open);
        };

        const onFocusIn = (e) => {
            if (isTextLikeInput(e.target)) setKeyboardOpen(true);
        };

        const onFocusOut = () => {
            // Delay so `document.activeElement` is updated.
            setTimeout(() => {
                const stillFocused = isTextLikeInput(document.activeElement);
                if (!stillFocused) setKeyboardOpen(false);
            }, 0);
        };

        document.addEventListener('focusin', onFocusIn);
        document.addEventListener('focusout', onFocusOut);

        const vv = window.visualViewport;
        let detachVv = null;
        if (vv && typeof vv.addEventListener === 'function') {
            const onVvChange = () => {
                // When keyboard opens, the *visual* viewport shrinks.
                const keyboardPx = window.innerHeight - vv.height;
                const keyboardOpen = keyboardPx > 140; // heuristic threshold
                if (keyboardOpen) {
                    setKeyboardOpen(true);
                } else if (!isTextLikeInput(document.activeElement)) {
                    setKeyboardOpen(false);
                }
            };

            vv.addEventListener('resize', onVvChange);
            vv.addEventListener('scroll', onVvChange);
            onVvChange();

            detachVv = () => {
                vv.removeEventListener('resize', onVvChange);
                vv.removeEventListener('scroll', onVvChange);
            };
        }

        return () => {
            document.removeEventListener('focusin', onFocusIn);
            document.removeEventListener('focusout', onFocusOut);
            if (detachVv) detachVv();
            setKeyboardOpen(false);
        };
    }, []);

    useEffect(() => {
        if (!ready) return;
        if (didAuthRequest.current) return;
        didAuthRequest.current = true;

        const fetchAuth = async () => {
            setAuthLoading(true);
            setAuthError(null);
            try {
                try {
                    WebApp.ready();
                } catch (_) {
                    // initData may still be available without ready().
                }

                const initDataRaw = getTelegramInitData();
                // const initDataRaw = "user=%7B%22id%22%3A5218704864%2C%22first_name%22%3A%22Omurzak%22%2C%22last_name%22%3A%22%22%2C%22username%22%3A%22omiiaow%22%2C%22language_code%22%3A%22ru%22%2C%22allows_write_to_pm%22%3Atrue%2C%22photo_url%22%3A%22https%3A%5C%2F%5C%2Ft.me%5C%2Fi%5C%2Fuserpic%5C%2F320%5C%2F1kf_OO8kFE5uCDdEsXnPZDp3EcFCwcxqT5wn-f2jO7v_pu8xH167Ya2uqgfFwxZN.svg%22%7D&chat_instance=-6228566680078583368&chat_type=sender&auth_date=1791213149&signature=-iibWDEnxBMT9t5MG9Xl4DRwwaWh_J-BwCsLgXaagw7jfMgmWQZFxbmwbtLflVwVE1K1dFVanDWSNtZUNIt9BA&hash=7c3ce8c47191f9c27d51d7632b485c5750483911aee1159a6b5f2f4cf7a83f4e"
                if (!initDataRaw) {
                    if (!token) {
                        setShowBrowserLogin(true);
                    }
                    return;
                }

                const response = await request("/operator/auth", "POST", { initDataRaw });
                if (response?.token) {
                    login(response.token);
                    return;
                }
                setAuthError("Не удалось войти. Откройте приложение через Telegram-бот.");
            } catch (_) {
                if (!token) {
                    setAuthError("Не удалось войти. Откройте приложение через Telegram-бот.");
                }
            } finally {
                setAuthLoading(false);
            }
        };

        fetchAuth();
    }, [ready, token, login, request, authRetryCount]);

    useEffect(() => {
        setupTelegramWebApp();
    }, []);

    const retryAuth = () => {
        didAuthRequest.current = false;
        setAuthError(null);
        setAuthLoading(true);
        setAuthRetryCount((count) => count + 1);
    };

    if (showBrowserLogin) {
        return <Navigate to="/login" replace />;
    }

    let app;
    if (isAuthenticated && !authLoading) {
        app = (
            <AuthContext.Provider value={authContextValue}>
                <ProviderRoutes />
            </AuthContext.Provider>
        );
    } else if (authError && !authLoading) {
        app = <AuthError message={authError} onRetry={retryAuth} />;
    } else {
        app = <Spinner />;
    }

    return <div className="app">{app}</div>;
}

export default App;